import { useEffect, useState } from 'react'
import PageProps from '../_types/page-props.interface'
import { useError } from '../context/error'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { LongTermService } from '../services/buyhold.service'
import { LongTerm, LongTermStock } from '../_types/longterm.type'
import { Formatters } from '../_helpers/formatter.helper'
import {
  Box,
  Button,
  Container,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Table,
  styled,
  TableCell,
  TableBody,
  TableSortLabel,
  Tooltip,
  Link,
  Grid,
  Chip,
  Tabs,
  Tab,
  IconButton,
  GlobalStyles
} from '@mui/material'
import KnowledgeBtn from '../components/controls/knowledge-btn'
import { StyleHelper } from '../components/style-helper'
import LoadingTable from '../components/loading/table.loading'
import { Cancel, Chat, CheckCircle, CloseRounded, Notes, Restore, Print } from '@mui/icons-material'
import StockCommentary from '../components/stock-commentary'
import TickerLink from '../components/ticker-link'
import { useGlobalState } from '../context/global'
import CarnivoreLogo from '../components/imgs/carnivore-logo'

export default function BuyHoldPage(props: PageProps) {
  useDocumentTitle(props.title)

  const { setError } = useError()
  const { user } = useGlobalState()
  const [loading, setLoading] = useState(true)
  
  const longTermService = new LongTermService(setError)
  const [longTerm, setLongTerm] = useState<LongTerm | null>(null)
  const [activeTab, setActiveTab] = useState(0);

  type SortDir = 'asc' | 'desc'
  type ActiveSortKey = 'symbol' | 'name' | 'entryDate' | 'entryPX' | 'currentPX' | 'gainLoss' | 'stopLoss' | 'buyUpTo' | 'inBuyZone'
  type ClosedSortKey = 'symbol' | 'name' | 'entryDate' | 'soldDate' | 'entryPX' | 'soldAt' | 'gainLoss'

  const [activeSort, setActiveSort] = useState<{ key: ActiveSortKey; dir: SortDir }>({ key: 'entryDate', dir: 'desc' })
  const [closedSort, setClosedSort] = useState<{ key: ClosedSortKey; dir: SortDir }>({ key: 'soldDate', dir: 'desc' })

  const _toggleActiveSort = (key: ActiveSortKey) => {
    setActiveSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })
  }
  const _toggleClosedSort = (key: ClosedSortKey) => {
    setClosedSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })
  }

  const _cmp = (a: any, b: any, dir: SortDir) => {
    const aNull = a === null || a === undefined || a === ''
    const bNull = b === null || b === undefined || b === ''
    if (aNull && bNull) return 0
    if (aNull) return 1
    if (bNull) return -1
    if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a
    const as = String(a).toLowerCase(); const bs = String(b).toLowerCase()
    if (as < bs) return dir === 'asc' ? -1 : 1
    if (as > bs) return dir === 'asc' ? 1 : -1
    return 0
  }

  const _activeSortValue = (s: LongTermStock, key: ActiveSortKey): any => {
    switch (key) {
      case 'symbol': return s.symbol
      case 'name': return s.name
      case 'entryDate': return new Date(s.entryDate).getTime()
      case 'entryPX': return s.entryPX
      case 'currentPX': return s.skipUpdate ? null : (s.currentPX ?? null)
      case 'gainLoss': return s.skipUpdate ? null : ((s.currentPX ?? 0) - s.entryPX) / s.entryPX
      case 'stopLoss': return (s.entryPX ?? 0) * 0.75
      case 'buyUpTo': return (s.entryPX ?? 0) * 1.25
      case 'inBuyZone': return s.skipUpdate ? null : (_isStockInBuy(s) ? 1 : 0)
    }
  }

  const _closedSortValue = (s: LongTermStock, key: ClosedSortKey): any => {
    switch (key) {
      case 'symbol': return s.symbol
      case 'name': return s.name
      case 'entryDate': return new Date(s.entryDate).getTime()
      case 'soldDate': return s.soldDate ? new Date(s.soldDate).getTime() : null
      case 'entryPX': return s.entryPX
      case 'soldAt': return s.soldAt ?? null
      case 'gainLoss': return ((s.soldAt ?? 0) - s.entryPX) / s.entryPX
    }
  }

  useEffect(() => {
    _getBuyHold()
  }, [])
  const _getBuyHold = async () => {
    setLoading(true)
    const res = await longTermService.getCurrentBuyHold()
    setLongTerm(res.longTerm)
    setLoading(false)

  }

  const _calculatePercentageChange = (
    initialValue: number,
    finalValue: number
  ): string => {
    const change = finalValue - initialValue
    return Formatters.formatPercentage((change / initialValue) * 100)
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }))

  const _scrollToCommentary = () => {
    const scrollTo = document.getElementById('carnivore-commentary')
    if (scrollTo) {
      scrollTo.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const _scrollToSold = () => {
    const scrollTo = document.getElementById('sold-longterm')
    if (scrollTo) {
      scrollTo.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const _isStockInBuy = (stock: LongTermStock) => {

    const entryPX = stock.entryPX ?? 0
    const buyUpTo = entryPX * 1.25
    const stopLoss = entryPX * 0.75

    const currentPX = stock.currentPX ?? 0

    return currentPX > stopLoss && currentPX < buyUpTo

  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', pb: 4 }}>
      <GlobalStyles
        styles={{
          '.print-only': {
            display: 'none !important'
          },
          '.print-watermark': {
            display: 'none !important'
          },
          '@media print': {
            body: {
              margin: 0,
              padding: 0
            },
            '.no-print': {
              display: 'none !important'
            },
            '.print-only': {
              display: 'block !important'
            },
            'nav': {
              display: 'none !important'
            },
            'aside': {
              display: 'none !important'
            },
            'header': {
              display: 'none !important'
            },
            '.MuiDrawer-root': {
              display: 'none !important'
            },
            '.MuiAppBar-root': {
              display: 'none !important'
            },
            '.MuiButton-root': {
              color: '#000 !important',
              backgroundColor: 'transparent !important',
              border: '1px solid #000 !important',
              textDecoration: 'underline !important'
            },
            '.MuiChip-root': {
              display: 'none !important'
            },
            '.MuiTooltip-tooltip': {
              display: 'none !important'
            },
            '.MuiTableContainer-root': {
              maxHeight: 'none !important',
              overflow: 'visible !important',
              boxShadow: 'none !important'
            },
            '.MuiPaper-root': {
              boxShadow: 'none !important',
              border: '1px solid #ddd'
            },
            '.MuiTableHead-root th': {
              position: 'static !important',
              backgroundColor: '#f5f5f5 !important',
              color: '#000 !important',
              border: '1px solid #ddd !important',
              padding: '8px !important'
            },
            '.MuiTableCell-root': {
              border: '1px solid #ddd !important',
              padding: '8px !important',
              fontSize: '11px !important'
            },
            '.MuiContainer-root': {
              maxWidth: '100% !important',
              padding: '0 !important'
            },
            '.MuiBox-root': {
              marginBottom: '20px !important',
              pageBreakInside: 'avoid'
            },
            'h5': {
              pageBreakAfter: 'avoid',
              marginTop: '15px',
              marginBottom: '8px',
              fontSize: '14px !important'
            },
            '.MuiTabs-root': {
              display: 'none !important'
            },
            '.print-watermark': {
              display: 'block !important',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: '65px',
              color: 'rgba(0, 0, 0, 0.03)',
              fontWeight: 'bold',
              zIndex: 99,
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            },
            '.MuiTable-root': {
              position: 'relative',
              zIndex: 1
            }
          }
        }}
      />
      <Box className="print-watermark">{user?.id}</Box>
      <Box className="print-only" sx={{ mb: 2, pb: 2, borderBottom: '2px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ width: 120, mb: 1 }}>
            <CarnivoreLogo />
          </Box>
          <Typography variant='h4'>Long Term Trades</Typography>
          <Typography variant='caption'>{new Date().toLocaleDateString()} - User ID: {user?.id}</Typography>
        </Box>
      </Box>
      <Grid container className="no-print">
        <Grid item xs={12} lg={10}>
          <Box display='flex' alignItems='center' gap={2}>
            <Typography variant='h4'>Long Term Trades</Typography>
            <KnowledgeBtn id='buy-hold' />
          </Box>
          <StyleHelper.SmallVerticalSpace />
          <Typography variant='body2'> The Long-term trades are Carnivore Trading's way of putting a handful of high-quality stock picks in your hands and, in turn, putting money in your pockets. Each of these power stocks has been hand-selected by Trader Z for the purpose of long-term trading (months, weeks). Throughout the year, we will continue to add and alert you of new stocks and any changes we might make! </Typography>
        </Grid>
        <Grid item xs={12} lg={2} textAlign={'right'} display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint} size="medium" color="primary">
              <Print />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      <StyleHelper.VerticalSpace />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="no-print">
        <Tabs value={activeTab} onChange={(event: React.SyntheticEvent, newValue: number) => {
          setActiveTab(newValue);
        }} aria-label="basic tabs example">
          <Tab label="Active Positions" />
          <Tab label="Closed Positions" />
          {/* <Tab label="Commentary" /> */}
        </Tabs>
      </Box>
      <Box className="print-only" sx={{ mb: 2 }}>
        <Typography variant='h5'>
          {activeTab === 0 ? 'Active Positions' : activeTab === 1 ? 'Closed Positions' : 'Commentary'}
        </Typography>
      </Box>
      <StyleHelper.VerticalSpace />

      {loading && <LoadingTable rowCount={7} columnCount={9} />}
      {!loading && longTerm && activeTab === 0 && (
        <Box>


          <TableContainer component={Paper} sx={{ maxHeight: '800px', overflow: 'auto' }}>
            <Table aria-label='buy & hold table'>
              <TableHead
                sx={{
                  '& th': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    position: 'sticky',  // Make the headers sticky
                    top: 0,              // Set it to stick at the top
                    zIndex: 20
                  }
                }}
              >
                <TableRow>
                  {([
                    { key: 'symbol', label: 'Symbol', align: 'left' },
                    { key: 'name', label: 'Name', align: 'right' },
                    { key: 'entryDate', label: 'Entry Date', align: 'right' },
                    { key: 'entryPX', label: 'Entry PX', align: 'right' },
                    { key: 'currentPX', label: 'Current PX', align: 'right' },
                    { key: 'gainLoss', label: 'Gain/Loss', align: 'right' },
                    { key: 'stopLoss', label: 'Stop Loss', align: 'right' },
                    { key: 'buyUpTo', label: 'Buy Up To', align: 'right' },
                    { key: 'inBuyZone', label: 'Carnivore Buy Zone', align: 'right' }
                  ] as { key: ActiveSortKey; label: string; align: 'left' | 'right' }[]).map(col => (
                    <TableCell key={col.key} align={col.align} sortDirection={activeSort.key === col.key ? activeSort.dir : false}>
                      <TableSortLabel
                        active={activeSort.key === col.key}
                        direction={activeSort.key === col.key ? activeSort.dir : 'asc'}
                        onClick={() => _toggleActiveSort(col.key)}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {longTerm.stocks.filter(stock => !stock.isSold).sort((a, b) => _cmp(_activeSortValue(a, activeSort.key), _activeSortValue(b, activeSort.key), activeSort.dir)).map(stock => (
                  <StyledTableRow
                    key={stock.id ?? `${stock.symbol}-${stock.entryDate}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell scope='row' align='right'>
                      {stock.customTickerUrl ? (
                        <Button
                          href={stock.customTickerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{textDecoration : 'underline', '&:hover' : {textDecoration : 'underline'}}}
                          variant='text'
                          color="secondary"
                        >
                          {stock.symbol}
                        </Button>
                      ) : (
                        <TickerLink display='link' ticker={stock.symbol} />
                      )}
                    </TableCell>
                    <TableCell align='right'>{stock.name}</TableCell>
                    <TableCell align='right'>
                      {new Date(stock.entryDate).toLocaleDateString('en-US')}
                    </TableCell>
                    <TableCell align='right'>
                      ${Formatters.formatMoney(stock.entryPX)}
                    </TableCell>
                    <TableCell align='right'>
                      {stock.skipUpdate ? '-' : `$${Formatters.formatMoney(stock.currentPX ?? 0)}`}
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        color: stock.skipUpdate ? 'text.secondary' :
                          stock.entryPX < (stock.currentPX ?? 0)
                            ? 'success.main'
                            : 'error.main'
                      }}
                    >
                      {stock.skipUpdate ? '-' : _calculatePercentageChange(
                        stock.entryPX,
                        stock.currentPX ?? 0
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      ${Formatters.formatMoney((stock.entryPX ?? 0) * 0.75)}
                    </TableCell>
                    <TableCell align='right'>
                      ${Formatters.formatMoney((stock.entryPX ?? 0) * 1.25)}
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        color: stock.skipUpdate ? 'text.secondary' :
                          _isStockInBuy(stock)
                            ? 'success.main'
                            : 'error.main'
                      }}
                    >
                      {stock.skipUpdate ? '-' : _isStockInBuy(stock)
                        ? (
                          <CheckCircle color='success' />
                        ) : (
                          <Cancel color='error' />
                        )}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <StyleHelper.DoubleVerticalSpace />
      {!loading && longTerm && activeTab === 1 && (
        <Box id="sold-longterm">

          <TableContainer component={Paper} sx={{ maxHeight: '800px', overflow: 'auto' }}>
            <Table aria-label='buy & hold table'>
              <TableHead
                sx={{
                  '& th': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    position: 'sticky',  // Make the headers sticky
                    top: 0,              // Set it to stick at the top
                    zIndex: 20
                  }
                }}
              >
                <TableRow>
                  {([
                    { key: 'symbol', label: 'Symbol', align: 'left' },
                    { key: 'name', label: 'Name', align: 'right' },
                    { key: 'entryDate', label: 'Entry Date', align: 'right' },
                    { key: 'soldDate', label: 'Close Date', align: 'right' },
                    { key: 'entryPX', label: 'Entry PX', align: 'right' },
                    { key: 'soldAt', label: 'Sold PX', align: 'right' },
                    { key: 'gainLoss', label: 'Gain/Loss', align: 'right' }
                  ] as { key: ClosedSortKey; label: string; align: 'left' | 'right' }[]).map(col => (
                    <TableCell key={col.key} align={col.align} sortDirection={closedSort.key === col.key ? closedSort.dir : false}>
                      <TableSortLabel
                        active={closedSort.key === col.key}
                        direction={closedSort.key === col.key ? closedSort.dir : 'asc'}
                        onClick={() => _toggleClosedSort(col.key)}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {longTerm.stocks.filter(stock => stock.isSold === true)?.sort((a, b) => _cmp(_closedSortValue(a, closedSort.key), _closedSortValue(b, closedSort.key), closedSort.dir)).map(stock => (
                  <StyledTableRow
                    key={stock.id ?? `${stock.symbol}-${stock.entryDate}-${stock.soldDate ?? ''}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell scope='row' align='right'>
                      <TickerLink display='link' ticker={stock.symbol} />
                    </TableCell>
                    <TableCell align='right'>{stock.name}</TableCell>
                    <TableCell align='right'>
                      {new Date(stock.entryDate).toLocaleDateString('en-US')}
                    </TableCell>
                    <TableCell align='right'>
                      {new Date(stock.soldDate ?? "").toLocaleDateString('en-US')}
                    </TableCell>
                    <TableCell align='right'>
                      ${Formatters.formatMoney(stock.entryPX)}
                    </TableCell>
                    <TableCell align='right'>
                      ${Formatters.formatMoney(stock.soldAt ?? 0)}
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        color:
                          stock.entryPX < (stock.soldAt ?? 0)
                            ? 'success.main'
                            : 'error.main'
                      }}
                    >
                      {_calculatePercentageChange(
                        stock.entryPX,
                        stock.soldAt ?? 0
                      )}
                    </TableCell>


                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {!loading && longTerm && activeTab === 2 && (
        <Box id="carnivore-commentary">

          <Typography variant='body2' gutterBottom>
            Click to expand and read Trader Z's Commentary on selected Buy &
            Hold Positions
          </Typography>
          {longTerm?.stocks
            .filter(s => s.commentary && s.commentary !== '' && s.isSold !== true)?.length === 0 && <Typography variant='body2' fontWeight={'bold'} textAlign={'start'} fontStyle={'italic'}>There is no active commentary.</Typography>}
          {longTerm?.stocks
            .filter(s => s.commentary && s.commentary !== '' && s.isSold !== true)
            .map(stock => (
              <StockCommentary
                ticker={stock.symbol}
                key={stock.symbol}
                commentary={stock.commentary ?? ''}
                name={stock.name}
              />
            ))}
        </Box>
      )}
    </Container>
  )
}